import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Building2, Users } from 'lucide-react';
import { getBzLoginEvents, type LoginEventRow, type LoginEventSource } from '../services/loginEventsService';

interface PrismeLoginLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatDateTimeFr(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: iso, time: '' };
  const date = d.toLocaleDateString('fr-CA'); // yyyy-mm-dd
  const time = d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return { date, time };
}

export default function PrismeLoginLogsModal({ isOpen, onClose }: PrismeLoginLogsModalProps) {
  const [active, setActive] = useState<LoginEventSource>('PRISME_BZ');
  const [days, setDays] = useState<number>(28);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<LoginEventRow[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setLoading(true);
    const effectiveDays = active === 'PRISME_EMPLOYE' ? days : undefined;
    getBzLoginEvents({ source: active, limit: 1000, days: effectiveDays })
      .then((r) => setRows(r))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Erreur chargement des logs'))
      .finally(() => setLoading(false));
  }, [isOpen, active, days]);

  const displayed = useMemo(() => {
    const copy = [...rows];
    // par défaut déjà trié DESC côté backend; on garde stable ici.
    return copy;
  }, [rows]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          className="bg-white rounded-2xl max-w-5xl w-full my-8 border border-gray-200 shadow-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-slate-900 via-bz-blue to-indigo-700 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/15 rounded-xl">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Logs de connexion PRIsME (BZ Inc.)</h2>
                <p className="text-white/80 text-sm">Date et heure de connexion — sources séparées</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors" type="button">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                type="button"
                onClick={() => setActive('PRISME_BZ')}
                className={`px-4 py-2 rounded-xl border transition-colors inline-flex items-center gap-2 ${
                  active === 'PRISME_BZ'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <Building2 className="w-4 h-4" />
                PRIsME BZ
              </button>
              <button
                type="button"
                onClick={() => setActive('PRISME_EMPLOYE')}
                className={`px-4 py-2 rounded-xl border transition-colors inline-flex items-center gap-2 ${
                  active === 'PRISME_EMPLOYE'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <Users className="w-4 h-4" />
                PRIsME Employés
              </button>
            </div>

            {active === 'PRISME_EMPLOYE' && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs text-gray-600">Période:</span>
                <select
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value, 10))}
                  className="text-xs px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                >
                  <option value={7}>7 jours</option>
                  <option value={14}>14 jours</option>
                  <option value={28}>4 semaines</option>
                  <option value={56}>8 semaines</option>
                </select>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
            )}

            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Usager (email)</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-700">Heure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td className="px-4 py-4 text-gray-500" colSpan={3}>
                          Chargement…
                        </td>
                      </tr>
                    ) : displayed.length === 0 ? (
                      <tr>
                        <td className="px-4 py-4 text-gray-500" colSpan={3}>
                          Aucun log pour cette source.
                        </td>
                      </tr>
                    ) : (
                      displayed.map((r) => {
                        const dt = formatDateTimeFr(r.logged_in_at);
                        return (
                          <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                            <td className="px-4 py-3 text-gray-900">{r.user_email}</td>
                            <td className="px-4 py-3 text-gray-700">{dt.date}</td>
                            <td className="px-4 py-3 text-gray-700">{dt.time}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Notes: “PRIsME Employés” se remplit via l’endpoint serveur dédié (appelé par l’app PRIsME Employé).
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

